import { useState, useEffect } from "react";
import { useSalesGetTask, useSalesGetProspect } from "@crowdcoursing/api/openapi";
import * as reactQuery from "@tanstack/react-query";
import { useToast } from "./useToast";

export const useTaskDetails = (taskId: string) => {
  const toast = useToast();
  const [prospectId, setProspectId] = useState<string | undefined>(undefined);

  // First, fetch the task details
  const {
    data: taskData,
    isLoading: isTaskLoading,
    error: taskError,
  } = useSalesGetTask(
    taskId ? {
      pathParams: { taskId },
    } : reactQuery.skipToken,
    {
      enabled: !!taskId,
    }
  );

  // Extract the prospectId from the task data when it's available
  useEffect(() => {
    if (taskData?.prospectId) {
      setProspectId(taskData.prospectId);
    }
  }, [taskData]);

  // Then, fetch the prospect details using the prospectId
  const {
    data: prospectData,
    isLoading: isProspectLoading,
    error: prospectError,
  } = useSalesGetProspect(
    prospectId ? {
      pathParams: { id: prospectId },
    } : reactQuery.skipToken,
    {
      enabled: !!prospectId,
    }
  );

  const isLoading = isTaskLoading || isProspectLoading;
  const error = taskError || prospectError;

  // If we have the prospect data, use it. Otherwise, if we have the task data with prospectName,
  // create a minimal prospect object with the name from the task.
  const prospect = prospectData || (taskData?.prospectName && taskData.prospectId ? {
    id: taskData.prospectId,
    name: taskData.prospectName,
    // Add minimal required fields to avoid errors in components expecting a full prospect
    stage: 0,
    type: "individual",
  } : null);

  // Show error toast if there's an error
  useEffect(() => {
    if (error) {
      toast.error("Failed to load task details");
    }
  }, [error, toast]);

  return {
    task: taskData,
    prospect,
    isLoading,
    error,
  };
};
